import { put, take, fork, call, select, all } from "redux-saga/effects";
import { ClaimAction, ClaimScope } from "./types";
import {
  enableScopeLoading,
  disableScopeLoading,
} from "../scope-loading/actions";
import { ScopeErrorAction } from "../scope-error/types";
import {
  makeHTMLContentForTicket,
  mapApiClaimData,
  mapApiClaimStepData,
  nextStepRedirect,
} from "./helpers";
import * as API from "../../service/api";
import { fetchClaimInfo, updateClaimState } from "./actions";
import axios from "axios";
import { RootState } from "../root-reducer";

function* upsertClaimFlow() {
  while (true) {
    const { payload = {}, step_num = 0 } = yield take(ClaimAction.UPSERT_CLAIM);
    yield put(enableScopeLoading(ClaimScope));
    try {
      const claimId = yield select((state) => state.claim.id);
      if (claimId) payload.id = claimId;
      const { details = {} } = yield call(API.fileClaim.upsertClaim, payload);
      if (details?.id) {
        payload.id = details.id;
        yield put(updateClaimState(mapApiClaimStepData(payload, step_num)));
        yield nextStepRedirect({ claimId: details?.id });
      }
    } catch (error) {
      yield put({
        type: ScopeErrorAction.SET_SCOPE_ERROR,
        payload: ClaimScope,
        error,
      });
    } finally {
      yield put(disableScopeLoading(ClaimScope));
    }
  }
}

function* removeClaimImageFlow() {
  while (true) {
    const { payload } = yield take(ClaimAction.REMOVE_CLAIM_IMAGE);
    yield put(enableScopeLoading(ClaimScope));
    try {
      const { fileName, fieldName } = payload;
      const claim = yield select((state: RootState) => state?.claim || {});
      const claimId = yield select((state: RootState) => state.claim.id);
      yield call(API.fileClaim.deleteClaimImage, claimId, fileName);
      const body = {
        id: claimId,
        [fieldName]: claim[fieldName]
          .filter((fileUrl) => !fileUrl.includes(fileName))
          .join("\n\n"),
      };
      yield call(API.fileClaim.upsertClaim, body);
      yield put(
        updateClaimState({
          [fieldName]: body[fieldName].split("\n\n").filter((data) => data),
        })
      );
    } catch (error) {
      yield put({
        type: ScopeErrorAction.SET_SCOPE_ERROR,
        payload: ClaimScope,
        error,
      });
    } finally {
      yield put(disableScopeLoading(ClaimScope));
    }
  }
}

function* uploadClaimImageFlow() {
  while (true) {
    const { payload = {} } = yield take(ClaimAction.UPLOAD_CLAIM_IMAGE);

    yield put(enableScopeLoading(ClaimScope));
    try {
      const { files, fieldName, isMultiple, storeKeyName } = payload;
      const claim = yield select((state: RootState) => state?.claim || {});
      const claimId = yield select((state: RootState) => state.claim.id);
      // delete already uploaded image
      if (!isMultiple) {
        const url = yield select(
          (state: RootState) => state?.claim[fieldName] || ""
        );
        if (url) {
          const fileName = url?.split("/")?.pop();
          yield call(API.fileClaim.deleteClaimImage, claimId, fileName);
        }
      }
      const uploadedUrlList: string[] = yield all(
        files.map(function* (file) {
          const uploadURL = yield call(
            API.fileClaim.getPresignedURL,
            claimId,
            file?.type
          );
          yield axios.put(uploadURL, file, {
            headers: {
              "Content-Type": file.type,
            },
          });
          const urlObj = new URL(uploadURL);
          urlObj.search = "";
          return urlObj.toString();
        })
      );
      const body = {
        id: claimId,
        [fieldName]: isMultiple
          ? [
              ...(claim?.[storeKeyName || fieldName] || []),
              ...uploadedUrlList,
            ].join("\n\n")
          : uploadedUrlList[0],
      };
      const { details = {} } = yield call(API.fileClaim.upsertClaim, body);
      if (details?.id) {
        yield put(
          updateClaimState({
            [storeKeyName || fieldName]: isMultiple
              ? body[fieldName].split("\n\n").filter((data) => data)
              : body[fieldName],
          })
        );
      }
    } catch (error) {
      console.log("error:", error);
      yield put({
        type: ScopeErrorAction.SET_SCOPE_ERROR,
        payload: ClaimScope,
        error,
      });
    } finally {
      yield put(disableScopeLoading(ClaimScope));
    }
  }
}

function* getClaimFlow() {
  while (true) {
    const request = yield take(ClaimAction.FETCH_CLAIM_INFO);
    yield put(enableScopeLoading(ClaimScope));
    try {
      let claimId = request.payload;
      claimId ||= yield select((state) => state.claim.id);
      const claimData = yield call(API.fileClaim.getClaim, claimId);
      if (claimData?.id) {
        yield put(updateClaimState(mapApiClaimData(claimData)));
      } else {
        throw new Error("CLAIM_NOT_FOUND");
      }
    } catch (error) {
      yield put({
        type: ScopeErrorAction.SET_SCOPE_ERROR,
        payload: ClaimScope,
        error: "CLAIM_NOT_FOUND",
      });
    } finally {
      yield put(disableScopeLoading(ClaimScope));
    }
  }
}

function* submitClaimFlow() {
  while (true) {
    yield take(ClaimAction.SUBMIT_CLAIM);
    yield put(enableScopeLoading(ClaimScope));
    try {
      const claimId = yield select((state) => state.claim.id);
      const claimName = yield select((state) => state?.claim.name || "");
      const data = yield call(API.fileClaim.submitClaim, claimId);
      if (data?.status === "success") {
        const claimData = yield select((state) => state.claim || {});
        const email = yield select(
          (state: RootState) => state?.claim?.email || "no@email.com"
        );
        yield call(API.fileClaim.createDeskTicket, {
          claimId,
          contact_email: email,
          subject: `Review New Mattress Claim: ${claimName}`,
          description: makeHTMLContentForTicket({ ...claimData }, claimName),
        });
      }
      if (!claimName) {
        yield put(fetchClaimInfo(claimId));
      }
      nextStepRedirect(6);
    } catch (error) {
      console.log("error:", error);
      yield put({
        type: ScopeErrorAction.SET_SCOPE_ERROR,
        payload: ClaimScope,
        error,
      });
    } finally {
      yield put(disableScopeLoading(ClaimScope));
    }
  }
}

export default function* claimSaga() {
  yield fork(upsertClaimFlow);
  yield fork(getClaimFlow);
  yield fork(submitClaimFlow);
  yield fork(removeClaimImageFlow);
  yield fork(uploadClaimImageFlow);
}
